import {useEffect} from 'react';
import {Waypoint} from 'react-waypoint';
import useTranslation from 'next-translate/useTranslation';
import {twMerge} from 'tailwind-merge';

import {
  JumpBackInHomepageExperimentVariation,
  useJumpBackInHomepage,
} from '@/components/global/providers/JumpBackInHomepageProvider';
import Carousel, {CarouselCard} from '@/components/ui/Carousel';
import useTracking from '@/hooks/useTracking';
import useValuePropsExperiment from '@/hooks/useValuePropsExperiment';
import {
  CARD_BASIS_CLASSNAMES,
  CARD_PADDING_CLASSNAMES,
  SLIDE_CONTAINER_CLASSNAMES,
} from './carouselClassNames';
import JumpBackInHomepageSkeleton from './JumpBackInHomepageSkeleton';
import ResumeCartCard from './ResumeCartCard';

type JumpBackInHomepageProps = {
  onEnter?: () => void;
  topOffset?: string;
};

const JumpBackInHomepage: React.FC<JumpBackInHomepageProps> = ({onEnter, topOffset}) => {
  const {variation, trackExperimentExposed, loading, hasFulfillmentDetails, cards} =
    useJumpBackInHomepage();
  const {t} = useTranslation('home');
  const {track} = useTracking();

  const {isTreatment: inValuePropsTreatment} = useValuePropsExperiment();

  useEffect(() => {
    if (hasFulfillmentDetails) {
      trackExperimentExposed();
    }
  }, [hasFulfillmentDetails, trackExperimentExposed]);

  useEffect(() => {
    if (variation === JumpBackInHomepageExperimentVariation.TREATMENT && hasFulfillmentDetails) {
      track('jump-back-in-homepage-shown', {
        page: window.location.pathname,
        url: window.location.href,
      });
    }
  }, [variation, hasFulfillmentDetails, track]);

  if (variation !== JumpBackInHomepageExperimentVariation.TREATMENT) return null;

  if (loading) return <JumpBackInHomepageSkeleton inValuePropsTreatment={inValuePropsTreatment} />;

  if (!hasFulfillmentDetails) return null;

  return (
    <Waypoint onEnter={onEnter} topOffset={topOffset}>
      <section
        className={twMerge(
          'mx-auto mb-12 w-full max-w-[1296px] px-4 pt-12 tablet:px-6',
          inValuePropsTreatment ? 'pt-[8.25rem] desktop:pt-[5.625rem]' : undefined, // ezx579::ARC::ValueProps
        )}

        data-testid="jump-back-in-homepage"
      >
        <Carousel
          slotProps={{
            controlsContainer: {className: 'hidden tablet:flex'},
            header: {className: 'items-center px-0 tablet:px-0'},
            slideContainer: {className: SLIDE_CONTAINER_CLASSNAMES},
            title: {className: 'tablet:text-2xl tablet:leading-8'},
          }}
          title={t('jumpBackInHomepage.heading')}
        >
          {cards.map((card, index) => (
            <CarouselCard
              basisClassNames={CARD_BASIS_CLASSNAMES}
              className={CARD_PADDING_CLASSNAMES}
              key={card.cart.id}
            >
              <ResumeCartCard
                cart={card.cart}
                fulfillmentDetail={card.fulfillmentDetail}
                index={index}
              />
            </CarouselCard>
          ))}
        </Carousel>
      </section>
    </Waypoint>
  );
};

export default JumpBackInHomepage;
